'use client';

import { useMemo, useState } from 'react';
import config from 'config';
import { defaultBasketData, ROUTES } from 'constant';
import { useRouter } from 'next/navigation';
import { useGetPackagesByLocationQuery } from 'services/packagesApi';
import { useGetShoppingCartQuery } from 'services/shoppingCartApi';

import { Box, Flex, useTheme, VStack } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setCartStep } from 'store/reducers/cartSlice';
import { setRedirectFlow } from 'store/reducers/userSlice';

import {
  ExtendedLocationPackageItem,
  LocationFlowType,
  LocationPackageInterface,
  ShopCartStep
} from 'types';

import { Divider, MultiStepHeader, SelectField, Text } from 'ui';
import { ContentLoader } from '../../../ContentLoader';
import { LocationButtonChange } from '../LocationButtonChange';
import { NavigationButtons } from '../NavigationButtons';
import { PackagesItem } from '../PackagesItem';
import { SubPackageItem } from '../SubPackageItem';

const {
  header: { shoppingCartType }
} = config;

const getUniqueByType = (array: LocationPackageInterface[]) =>
  array
    .reduce((acc, item) => {
      if (!acc?.find((obj) => obj?.category === item?.category)) {
        acc.push(item);
      }
      return acc;
    }, [] as LocationPackageInterface[])
    .map((item) => ({ label: item?.category, value: item?.category }));

const {
  shop: {
    isCommerceAvailable,
    packages: {
      packages: { title, subtitle, paddings }
    }
  }
} = config;

interface PackagesListStepProps {
  onForward: () => void;
  onBack: () => void;
  promoId: number;
}

const PackagesListStep = ({
  onForward,
  onBack,
  promoId
}: PackagesListStepProps) => {
  const {
    colors: {
      palette: {
        shop: {
          packages: { stepPackages }
        }
      }
    }
  } = useTheme();
  const router = useRouter();

  const dispatch = useAppDispatch();
  const { locations } = useAppSelector((state) => state.locations);
  const { selectedLocation } = useAppSelector((state) => state.user);
  const { userInfo } = useAppSelector((state) => state.persistAuth);
  const { selectedPackages: selectedLocalPackages } = useAppSelector(
    (state) => state.cart
  );
  const isLogged = Boolean(userInfo?.clid) && Boolean(userInfo?.token);
  const locationId = selectedLocation?.id || 0;
  const isSingleLocation = locations.length < 2;

  const { data: shopData = defaultBasketData } = useGetShoppingCartQuery('', {
    skip: !isLogged
  });
  const { data: currentPackages = [], isLoading } =
    useGetPackagesByLocationQuery(
      { locationId, promoId },
      { skip: locationId === 0 }
    ) || {};

  const selectedPackages: number[] = useMemo(
    () => shopData.items.map((item) => item.id),
    [shopData]
  );
  const hasSelectedItems: boolean = useMemo(
    () =>
      (currentPackages ?? []).some((item) =>
        selectedPackages.includes(item.id)
      ),
    [currentPackages, selectedPackages]
  );

  const [categoryFilter, setCategoryFilter] = useState<string>('');

  const packages = useMemo(
    () =>
      categoryFilter === ''
        ? currentPackages
        : (currentPackages ?? []).filter(
            (pack) => pack.category === categoryFilter
          ),
    [categoryFilter, currentPackages]
  );
  const isPackagesExist = packages.length > 0;

  const onBackHandler = () => router.push(ROUTES.shop);

  const filterList = useMemo(
    () => getUniqueByType(currentPackages ?? []),
    [currentPackages]
  );

  const options = [{ value: '', label: 'SHOW ALL' }, ...filterList];

  const handleSelectChange = (_: string, value: string) => {
    setCategoryFilter(value);
  };

  const onCart = () => {
    if (!isLogged) {
      dispatch(
        setRedirectFlow(
          isCommerceAvailable
            ? LocationFlowType.BASKET
            : LocationFlowType.PACKAGES
        )
      );
    }
    if (isCommerceAvailable) {
      dispatch(setCartStep(ShopCartStep.DETAILS));
      router.push(ROUTES.cart);
    } else {
      onForward();
    }
  };

  return (
    <Box>
      <MultiStepHeader
        title={title}
        subTitle={subtitle}
        headingColor={stepPackages.heading}
        descriptionColor={stepPackages.subtitle}
        mt={0}
        mb={{ base: '20px', md: '40px' }}
        maxW='auto'
        descriptionPX={paddings}
      />
      {isCommerceAvailable && (
        <LocationButtonChange
          bg={stepPackages.locationButtonBg}
          locationName={
            (selectedLocation?.city || selectedLocation?.full_name) ?? ''
          }
          onLocatioStep={onBack}
          isSingleLocation={isSingleLocation}
        />
      )}
      {filterList.length > 1 && (
        <VStack
          w='full'
          bg={stepPackages.filterBg}
          py='13.5px'
          px={['12px', '15px', '20px']}
        >
          <Text
            textTransform='uppercase'
            textAlign='left'
            w='full'
            color={stepPackages.filterTextColor}
          >
            Selected Treatment Category
          </Text>
          <Flex
            flexDirection={{ base: 'column', md: 'row' }}
            alignItems='center'
            w='full'
          >
            <SelectField
              name='filter'
              validateField={() => {}}
              setFieldValue={handleSelectChange}
              setFieldTouched={() => {}}
              value={categoryFilter}
              options={options}
              touched
              isSearchable
              bordered
              stylesControl={{ height: '60px', fontSize: '21px' }}
              stylesOption={{ fontSize: '21px' }}
              formControlProps={{
                sx: {
                  '& label': { fontSize: '12px' }
                }
              }}
            />
            <Text
              textAlign='right'
              w='full'
              fontSize={{ base: '14px', md: '18px' }}
              pt={{ base: 2, md: 0 }}
              color={stepPackages.filterTextColor}
            >
              {`Showing ${packages.length} package${
                packages.length > 1 ? 's' : ''
              }`}
            </Text>
          </Flex>
        </VStack>
      )}

      <Divider
        height={{ base: '1px', md: '2px' }}
        bg={stepPackages.divider}
        border='none'
      />

      <Box w='100%'>
        {isLoading || !isPackagesExist ? (
          <ContentLoader isLoading={isLoading} label='No packages found' />
        ) : (
          packages.map(
            (item: LocationPackageInterface | ExtendedLocationPackageItem) => {
              if (item.is_pkg_master === 1) {
                return (
                  <PackagesItem
                    key={item.id}
                    item={item as LocationPackageInterface}
                    selectedPackages={selectedPackages}
                    locationId={locationId}
                  />
                );
              }
              return (
                <SubPackageItem
                  key={item.id}
                  item={item as ExtendedLocationPackageItem}
                  locationId={locationId}
                />
              );
            }
          )
        )}
      </Box>

      <NavigationButtons
        onBackHandler={onBackHandler}
        onNextHandler={onCart}
        backLabel='Back'
        nextLabel={`Proceed to ${
          isCommerceAvailable ? shoppingCartType : 'cart'
        }`}
        isDisabled={
          isLogged ? !hasSelectedItems : selectedLocalPackages.length === 0
        }
      />
    </Box>
  );
};

export default PackagesListStep;
